import { defineStore } from 'pinia';
import { catchError, defer, finalize, firstValueFrom, forkJoin, map, of, switchMap, tap, throwError } from 'rxjs';
import { useMerchantStore } from '~/store/merchant';
import { useNotificationStore } from '~/store/notification';
import { useUserStore } from '~/store/user';
import { auth } from '~/support/constants';
import { LoginResponse, RefreshResponse } from '~/types';

export const useAuthStore = defineStore('auth', () => {
  const { identity } = useGateway();
  const authenticated =
    useCookie<boolean>(auth.cookies.AUTHENTICATED, {
      sameSite: 'none',
      secure: true,
    }) ?? false;
  const refreshToken = useCookie(auth.cookies.REFRESH_TOKEN, {
    sameSite: 'none',
    secure: true,
  });
  const accessToken = ref<string | null>(null);
  const merchant = useMerchantStore();
  const user = useUserStore();
  const notification = useNotificationStore();

  const mfaRequired = ref(false);
  const mfaToken = ref<string | null>(null);
  const mfaOptions = ref<string[]>([]);
  const mfaEnrollment = ref<any | null>(null);
  const email = ref<string | undefined>(undefined);

  function clearMfa() {
    mfaRequired.value = false;
    mfaToken.value = null;
    mfaOptions.value = [];
    mfaEnrollment.value = null;
    email.value = undefined;
  }

  function clearUser() {
    authenticated.value = false;
    refreshToken.value = null;
    accessToken.value = null;
  }

  function handleAuthResponse(res: LoginResponse | RefreshResponse) {
    accessToken.value = res.access_token;

    if ('refresh_token' in res) {
      refreshToken.value = res.refresh_token;
    }

    authenticated.value = true;
  }

  const refresh$ = defer(() => {
    return identity.auth.refresh(refreshToken.value as string).pipe(
      catchError(error => {
        console.error(error);
        throw error;
      }),
      tap(res => {
        if (res?.data) {
          handleAuthResponse(res.data);
        }
      }),
    );
  });

  const logout$ = defer(() => {
    if (!refreshToken.value) {
      return of(true);
    }

    return identity.auth.logout(refreshToken.value as string).pipe(
      finalize(() => {
        clearUser();
      }),
    );
  });

  async function refresh() {
    if (!refreshToken.value) {
      clearUser();

      return await firstValueFrom(of(null));
    }

    return await firstValueFrom(
      refresh$.pipe(
        catchError(error => {
          console.error('unable to refresh token', error);
          clearUser();

          return of(null);
        }),
        tap(res => {
          if (res?.data) {
            handleAuthResponse(res.data);

            return res.data;
          }
        }),
      ),
    );
  }

  function login(credentials: { email: string; password: string }) {
    return identity.auth.login(credentials.email, credentials.password).pipe(
      switchMap(res => {
        const data = res?.data;
        if (!data) return of(null);

        if (data.mfaRequired) {
          setMfa({ ...data, email: credentials.email });

          return of({ availableAuthMethods: data.availableAuthMethods, mfaRequired: true });
        }

        return handlePostLogin(data);
      }),
    );
  }

  function setMfa(data: LoginResponse & { email: string }) {
    // Clear stale refresh token to prevent using pre-MFA token after MFA completes
    refreshToken.value = null;
    mfaRequired.value = data.mfaRequired;
    mfaToken.value = data.mfaToken || null;
    mfaOptions.value = data.mfaOptions || [];
    mfaEnrollment.value = data.enrollment || null;
    email.value = data.email;
  }

  function logout() {
    return logout$;
  }

  function postLogin() {
    return forkJoin([merchant.loadStores(), user.fetchUserInfo(), notification.initialize()]);
  }

  function handlePostLogin(data: LoginResponse) {
    handleAuthResponse(data);

    return postLogin().pipe(map(postResult => ({ postData: postResult })));
  }

  function handleDriExchange(driToken: string) {
    return identity.auth.exchangeDri(driToken).pipe(
      catchError(error => {
        console.error('DRI exchange failed:', error);
        throw error;
      }),
      map(response => response.data),
      switchMap(data => handlePostLogin(data)),
      map(() => true),
    );
  }

  function handleFluidSessionExchange(sessionToken: string, dri: string) {
    return identity.auth.exchangeFluidSession(sessionToken, dri).pipe(
      catchError(error => {
        console.error('Fluid session exchange failed:', error);
        throw error;
      }),
      map(response => response.data),
      switchMap(data => handlePostLogin(data)),
      map(() => true),
    );
  }

  function handleShopifyLoginExchange(shopifyLoginToken: string) {
    return identity.auth.exchangeShopifyLogin(shopifyLoginToken).pipe(
      catchError(error => {
        console.error('Shopify login exchange failed:', error);
        throw error;
      }),
      map(response => response.data),
      switchMap(data => {
        if (data && 'access_token' in data) {
          return handlePostLogin(data).pipe(map(() => data));
        }

        return of(data);
      }),
    );
  }

  return {
    accessToken,
    authenticated,
    clearMfa,
    clearUser,
    email,
    exchangeDriToken: handleDriExchange,
    exchangeFluidSession: handleFluidSessionExchange,
    exchangeShopifyLoginToken: handleShopifyLoginExchange,
    handlePostLogin,
    login,
    logout,
    mfaEnrollment,
    mfaOptions,
    mfaRequired,
    mfaToken,
    postLogin,
    refresh,
    refreshToken,
  };
});
